import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Container,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Switch,
  FormControlLabel,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  CheckCircle,
  Error,
  Warning,
  Refresh,
  Storage,
  Cloud,
  Speed,
  Timer,
} from '@mui/icons-material';
import axios from 'axios';

interface CollectionHealth {
  name: string;
  displayName: string;
  status: 'healthy' | 'warning' | 'error' | 'checking';
  documentCount: number;
  lastChecked: Date | null;
  responseTime: number;
  errorMessage?: string;
}

interface ApiEndpointHealth {
  name: string;
  path: string;
  description?: string;
  status: 'healthy' | 'warning' | 'error' | 'checking';
  responseTime: number;
  lastChecked: Date | null;
  errorMessage?: string;
}

// Firebase Collections used by iOS app
const FIREBASE_COLLECTIONS = [
  { name: 'users', displayName: 'Kullanıcılar' },
  { name: 'matches', displayName: 'Eşleşmeler' },
  { name: 'chats', displayName: 'Sohbetler' },
  { name: 'messages', displayName: 'Mesajlar' },
  { name: 'posts', displayName: 'Gönderiler' },
  { name: 'comments', displayName: 'Yorumlar' },
  { name: 'likes', displayName: 'Beğeniler' },
  { name: 'swipes', displayName: 'Swipe\'lar' },
  { name: 'notifications', displayName: 'Bildirimler' },
  { name: 'userLocations', displayName: 'Kullanıcı Konumları' },
  { name: 'partnerCafes', displayName: 'Partner Kafeler' },
  { name: 'favoriteCafes', displayName: 'Favori Kafeler' },
  { name: 'cafe_ratings', displayName: 'Kafe Puanları' },
  { name: 'cafe_favorite_counts', displayName: 'Kafe Favori Sayıları' },
  { name: 'friendships', displayName: 'Arkadaşlıklar' },
  { name: 'friendRequests', displayName: 'Arkadaşlık İstekleri' },
  { name: 'follows', displayName: 'Takipler' },
  { name: 'blocks', displayName: 'Engellemeler' },
  { name: 'user_reports', displayName: 'Kullanıcı Raporları' },
  { name: 'content_reports', displayName: 'İçerik Raporları' },
  { name: 'premiumUsers', displayName: 'Premium Kullanıcılar' },
  { name: 'anonymousChats', displayName: 'Anonim Sohbetler' },
  { name: 'anonymousMatches', displayName: 'Anonim Eşleşmeler' },
  { name: 'anonymousUsers', displayName: 'Anonim Kullanıcılar' },
  { name: 'dailyGames', displayName: 'Günlük Oyunlar' },
  { name: 'leaderboard', displayName: 'Liderlik Tablosu' },
  { name: 'monthlyWinners', displayName: 'Aylık Kazananlar' },
  { name: 'questionBank', displayName: 'Soru Bankası' },
  { name: 'deletedAccounts', displayName: 'Silinmiş Hesaplar' },
  { name: 'user_swipe_limits', displayName: 'Swipe Limitleri' },
  { name: 'user_undo_limits', displayName: 'Geri Alma Limitleri' },
  { name: 'user_post_limits', displayName: 'Gönderi Limitleri' },
  { name: 'social_notifications', displayName: 'Sosyal Bildirimler' },
];

// Critical API endpoints to monitor
const API_ENDPOINTS = [
  { name: 'Sunucu Sağlık', path: '/api/health', description: 'Backend sunucusu çalışıyor mu?' },
  { name: 'Kullanıcılar', path: '/api/users?page=1&limit=1', description: 'Kullanıcı listesi erişimi' },
  { name: 'Eşleşmeler', path: '/api/matches?page=1&limit=1', description: 'Eşleşme verileri' },
  { name: 'Bildirimler', path: '/api/notifications?page=1&limit=1', description: 'Push bildirimleri' },
  { name: 'Raporlar', path: '/api/reports?page=1&limit=1', description: 'Kullanıcı şikayetleri' },
  { name: 'Konumlar', path: '/api/locations?page=1&limit=1', description: 'Konum verileri (yavaş olabilir)' },
  { name: 'Gönderiler', path: '/api/posts?page=1&limit=1', description: 'Sosyal medya postları' },
  { name: 'Geri Bildirimler', path: '/api/feedbacks?page=1&limit=1', description: 'Kullanıcı geri bildirimleri' },
  { name: 'Dashboard', path: '/api/dashboard/comprehensive-stats', description: 'Genel istatistikler' },
  { name: 'Analitik', path: '/api/analytics/overview', description: 'Detaylı analiz verileri' },
];

const ApiHealthMonitorPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastFullCheck, setLastFullCheck] = useState<Date | null>(null);
  const [collections, setCollections] = useState<CollectionHealth[]>(
    FIREBASE_COLLECTIONS.map(c => ({
      ...c,
      status: 'checking' as const,
      documentCount: 0,
      lastChecked: null,
      responseTime: 0,
    }))
  );
  const [apiEndpoints, setApiEndpoints] = useState<ApiEndpointHealth[]>(
    API_ENDPOINTS.map(e => ({
      ...e,
      status: 'checking' as const,
      responseTime: 0,
      lastChecked: null,
    }))
  );

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const checkApiEndpoints = useCallback(async () => {
    const results: ApiEndpointHealth[] = [];

    for (const endpoint of API_ENDPOINTS) {
      const startTime = Date.now();
      try {
        await axios.get(`https://api.sobcof.com${endpoint.path}`, {
          ...getAuthHeaders(),
          timeout: 10000,
        });
        results.push({
          ...endpoint,
          status: 'healthy',
          responseTime: Date.now() - startTime,
          lastChecked: new Date(),
        });
      } catch (error: any) {
        const responseTime = Date.now() - startTime;
        results.push({
          ...endpoint,
          status: responseTime > 5000 ? 'warning' : 'error',
          responseTime,
          lastChecked: new Date(),
          errorMessage: error.message,
        });
      }
    }

    setApiEndpoints(results);
  }, []);

  const checkCollections = useCallback(async () => {
    try {
      const response = await axios.get(
        'https://api.sobcof.com/api/health/collections',
        getAuthHeaders()
      );

      if (response.data.success) {
        const healthData = response.data.data;
        setCollections(prev =>
          prev.map(col => {
            const data = healthData[col.name];
            if (data) {
              return {
                ...col,
                status: data.accessible ? 'healthy' : 'error',
                documentCount: data.count || 0,
                lastChecked: new Date(),
                responseTime: data.responseTime || 0,
                errorMessage: data.error,
              };
            }
            return { ...col, status: 'warning', lastChecked: new Date() };
          })
        );
      }
    } catch (error) {
      // If collection health endpoint doesn't exist, mark all as unknown
      setCollections(prev =>
        prev.map(col => ({
          ...col,
          status: 'warning',
          lastChecked: new Date(),
          errorMessage: 'Health endpoint not available',
        }))
      );
    }
  }, []);

  const runHealthCheck = useCallback(async () => {
    setLoading(true);
    await Promise.all([checkApiEndpoints(), checkCollections()]);
    setLastFullCheck(new Date());
    setLoading(false);
  }, [checkApiEndpoints, checkCollections]);

  useEffect(() => {
    runHealthCheck();
  }, [runHealthCheck]);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(runHealthCheck, 300000); // Her 5 dakikada bir
      return () => clearInterval(interval);
    }
  }, [autoRefresh, runHealthCheck]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle sx={{ color: '#4caf50' }} />;
      case 'warning':
        return <Warning sx={{ color: '#ff9800' }} />;
      case 'error':
        return <Error sx={{ color: '#f44336' }} />;
      default:
        return <CircularProgress size={20} />;
    }
  };

  const getCollectionDescription = (name: string): string => {
    const descriptions: { [key: string]: string } = {
      'users': 'Tüm kayıtlı kullanıcıların profil bilgileri',
      'matches': 'Karşılıklı beğeni sonucu oluşan eşleşmeler',
      'chats': 'Kullanıcılar arası sohbet odaları',
      'messages': 'Sohbetlerdeki mesajlar',
      'posts': 'Kullanıcıların paylaştığı gönderiler',
      'comments': 'Gönderilere yapılan yorumlar',
      'likes': 'Gönderi beğenileri',
      'swipes': 'Kullanıcı beğeni/reddetme hareketleri',
      'notifications': 'Push bildirim kayıtları',
      'userLocations': 'Kullanıcı konum bilgileri',
      'partnerCafes': 'Anlaşmalı kafe listesi',
      'favoriteCafes': 'Kullanıcıların favori kafeleri',
      'cafe_ratings': 'Kafe puanlamaları',
      'cafe_favorite_counts': 'Kafelerin favori sayıları',
      'friendships': 'Arkadaşlık ilişkileri',
      'friendRequests': 'Bekleyen arkadaşlık istekleri',
      'follows': 'Takip ilişkileri',
      'blocks': 'Engellenen kullanıcılar',
      'user_reports': 'Kullanıcı şikayetleri',
      'content_reports': 'İçerik şikayetleri',
      'premiumUsers': 'Premium abonelik bilgileri',
      'anonymousChats': 'Anonim sohbetler',
      'anonymousMatches': 'Anonim eşleşmeler',
      'anonymousUsers': 'Anonim kullanıcı profilleri',
      'dailyGames': 'Günlük oyun verileri',
      'leaderboard': 'Oyun liderlik tablosu',
      'monthlyWinners': 'Aylık oyun kazananları',
      'questionBank': 'Oyun soru havuzu',
      'deletedAccounts': 'Silinen hesap kayıtları',
      'user_swipe_limits': 'Günlük swipe limitleri',
      'user_undo_limits': 'Geri alma limitleri',
      'user_post_limits': 'Gönderi paylaşım limitleri',
      'social_notifications': 'Sosyal etkileşim bildirimleri',
    };
    return descriptions[name] || '-';
  };

  const healthyApis = apiEndpoints.filter(e => e.status === 'healthy').length;
  const healthyCollections = collections.filter(c => c.status === 'healthy').length;
  const totalApis = apiEndpoints.length;
  const totalCollections = collections.length;

  const overallHealth =
    healthyApis === totalApis && healthyCollections === totalCollections
      ? 'healthy'
      : healthyApis < totalApis * 0.5 || healthyCollections < totalCollections * 0.5
      ? 'error'
      : 'warning';

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <AppBar position="static" sx={{ backgroundColor: '#8B4513' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate(-1)}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, ml: 2 }}>
            API & Database Health Monitor
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                color="default"
              />
            }
            label="Otomatik Yenile"
            sx={{ color: 'white', mr: 2 }}
          />
          <IconButton color="inherit" onClick={runHealthCheck} disabled={loading}>
            <Refresh />
          </IconButton>
          <Avatar sx={{ bgcolor: '#D2691E', ml: 2 }}>
            <Cloud />
          </Avatar>
        </Toolbar>
      </AppBar>

      {loading && <LinearProgress sx={{ backgroundColor: '#D2691E' }} />}

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Overall Status */}
        <Alert
          severity={overallHealth === 'healthy' ? 'success' : overallHealth === 'warning' ? 'warning' : 'error'}
          sx={{ mb: 3 }}
          icon={getStatusIcon(overallHealth)}
        >
          <Typography variant="h6">
            {overallHealth === 'healthy'
              ? '✅ Tüm Sistemler Çalışıyor'
              : overallHealth === 'warning'
              ? '⚠️ Bazı Servisler Yavaş veya Sorunlu'
              : '❌ Kritik Sorunlar Var'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Son kontrol:</strong> {lastFullCheck?.toLocaleTimeString('tr-TR') || '-'} |
            <strong> Backend API:</strong> {healthyApis}/{totalApis} çalışıyor |
            <strong> Veritabanı:</strong> {healthyCollections}/{totalCollections} erişilebilir
          </Typography>
        </Alert>

        {/* Legend */}
        <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="body2" fontWeight="bold">Renk Kodları:</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CheckCircle sx={{ color: '#4caf50', fontSize: 18 }} />
            <Typography variant="body2">Çalışıyor (&lt;500ms)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Warning sx={{ color: '#ff9800', fontSize: 18 }} />
            <Typography variant="body2">Yavaş (500-2000ms)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Error sx={{ color: '#f44336', fontSize: 18 }} />
            <Typography variant="body2">Hata veya Timeout (&gt;2000ms)</Typography>
          </Box>
        </Paper>

        {/* Summary Cards */}
        <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
          <Card sx={{ flex: 1, minWidth: 220 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: healthyApis === totalApis ? '#4caf50' : '#ff9800', width: 56, height: 56 }}>
                  <Cloud />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">{healthyApis}/{totalApis}</Typography>
                  <Typography color="text.secondary">Backend Servisleri</Typography>
                  <Typography variant="caption" color={healthyApis === totalApis ? 'success.main' : 'warning.main'}>
                    {healthyApis === totalApis ? 'Tümü çalışıyor' : `${totalApis - healthyApis} sorunlu`}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ flex: 1, minWidth: 220 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: healthyCollections === totalCollections ? '#4caf50' : '#ff9800', width: 56, height: 56 }}>
                  <Storage />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">{healthyCollections}/{totalCollections}</Typography>
                  <Typography color="text.secondary">Veritabanı Tabloları</Typography>
                  <Typography variant="caption" color={healthyCollections === totalCollections ? 'success.main' : 'warning.main'}>
                    {healthyCollections === totalCollections ? 'Tümü erişilebilir' : `${totalCollections - healthyCollections} sorunlu`}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ flex: 1, minWidth: 220 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{
                  bgcolor: apiEndpoints.reduce((a, b) => a + b.responseTime, 0) / apiEndpoints.length < 1000 ? '#4caf50' : '#ff9800',
                  width: 56, height: 56
                }}>
                  <Speed />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {apiEndpoints.length > 0
                      ? Math.round(apiEndpoints.reduce((a, b) => a + b.responseTime, 0) / apiEndpoints.length)
                      : 0}ms
                  </Typography>
                  <Typography color="text.secondary">Ortalama Yanıt Süresi</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {apiEndpoints.reduce((a, b) => a + b.responseTime, 0) / apiEndpoints.length < 500 ? 'Hızlı' :
                     apiEndpoints.reduce((a, b) => a + b.responseTime, 0) / apiEndpoints.length < 2000 ? 'Normal' : 'Yavaş'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ flex: 1, minWidth: 220 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: '#8B4513', width: 56, height: 56 }}>
                  <Timer />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">{autoRefresh ? '5dk' : 'Manuel'}</Typography>
                  <Typography color="text.secondary">Kontrol Modu</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {autoRefresh ? 'Otomatik kontrol açık' : 'Yenile butonuna tıklayın'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* API Endpoints Table */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Cloud /> Backend API Servisleri
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Uygulamanın kullandığı tüm backend servisleri. Yeşil = çalışıyor, Sarı = yavaş, Kırmızı = sorunlu.
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell width={60}>Durum</TableCell>
                    <TableCell>Servis</TableCell>
                    <TableCell>Açıklama</TableCell>
                    <TableCell align="right" width={120}>Yanıt Süresi</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {apiEndpoints.map((endpoint) => (
                    <TableRow
                      key={endpoint.path}
                      hover
                      sx={{
                        backgroundColor: endpoint.status === 'error' ? 'rgba(244, 67, 54, 0.05)' :
                          endpoint.status === 'warning' ? 'rgba(255, 152, 0, 0.05)' : 'inherit'
                      }}
                    >
                      <TableCell>
                        <Tooltip title={
                          endpoint.status === 'healthy' ? 'Çalışıyor' :
                          endpoint.status === 'warning' ? 'Yavaş yanıt' :
                          endpoint.status === 'error' ? 'Hata: ' + (endpoint.errorMessage || 'Bağlantı kurulamadı') :
                          'Kontrol ediliyor...'
                        }>
                          {getStatusIcon(endpoint.status)}
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {endpoint.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                          {endpoint.path}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {endpoint.description || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={endpoint.responseTime >= 10000 ? 'Timeout' : `${endpoint.responseTime}ms`}
                          size="small"
                          color={endpoint.responseTime < 500 ? 'success' : endpoint.responseTime < 2000 ? 'warning' : 'error'}
                          sx={{ fontWeight: 'bold' }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Firebase Collections Table */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Storage /> Firebase Veritabanı Tabloları
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              iOS uygulamasının kullandığı tüm Firebase veritabanı tabloları. Döküman sayısı o tablodaki kayıt sayısını gösterir.
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell width={60}>Durum</TableCell>
                    <TableCell>Tablo Adı</TableCell>
                    <TableCell>Ne İçin Kullanılıyor?</TableCell>
                    <TableCell align="right" width={120}>Kayıt Sayısı</TableCell>
                    <TableCell align="right" width={100}>Hız</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {collections.map((col) => (
                    <TableRow
                      key={col.name}
                      hover
                      sx={{
                        backgroundColor: col.status === 'error' ? 'rgba(244, 67, 54, 0.05)' : 'inherit'
                      }}
                    >
                      <TableCell>
                        <Tooltip title={
                          col.status === 'healthy' ? 'Erişilebilir' :
                          col.status === 'error' ? 'Hata: ' + (col.errorMessage || 'Erişilemiyor') :
                          'Kontrol ediliyor...'
                        }>
                          {getStatusIcon(col.status)}
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {col.displayName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                          {col.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {getCollectionDescription(col.name)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {col.documentCount > 0 ? (
                          <Chip
                            label={col.documentCount.toLocaleString()}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">Boş</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {col.responseTime > 0 ? (
                          <Chip
                            label={`${col.responseTime}ms`}
                            size="small"
                            color={col.responseTime < 500 ? 'success' : col.responseTime < 2000 ? 'warning' : 'error'}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default ApiHealthMonitorPage;
